import { QueryFunction } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { ProductDetails } from 'src/types/catalog/ProductDetails';
import axiosInstance from 'src/utils/axios';
import { getProductImageSrc, parseProductImageUrl } from 'src/utils/parseProductImageUrl';

/**
 * Shared query function for fetching product details.
 * Used by both the hook and direct queryClient.fetchQuery calls.
 * Accepts both nullable and non-nullable query keys for flexibility.
 */
export const fetchProductDetailsQuery: QueryFunction<ProductDetails> = async ({
  queryKey,
  signal,
}) => {
  const [, , productId, catalogId, categoryId] = queryKey;

  // Type guard to ensure we have valid IDs
  if (
    typeof productId !== 'number' ||
    typeof catalogId !== 'number' ||
    typeof categoryId !== 'number'
  ) {
    throw new Error(
      'Invalid product details parameters: productId, catalogId, and categoryId must be numbers'
    );
  }

  const res = await axiosInstance.get<ProductDetails>(
    api.getProductDetails(productId, catalogId, categoryId),
    { signal }
  );

  // Transform the URL using the parseProductImageUrl function
  const transformedUrl = getProductImageSrc(res.data.url, {
    productDescription: res.data.imageDescription,
    viewType: 'detail',
  });
  const parsedUrl = parseProductImageUrl(res.data.url);

  return {
    ...res.data,
    url: transformedUrl,
    ...(parsedUrl as any),
    productKey: `${catalogId}-${categoryId}-${productId}`,
  };
};
